import { FormikContextType, useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { AppState } from 'models/app-store';
import { IWhoAmIResponse } from 'models/http/auth';
import { IUpdateUserRequest } from 'models/http/user';
import { ISession } from 'models/session';
import * as authActions from 'store/actions/auth';
import * as uiActions from 'store/actions/ui';
import { GenderTypes } from 'utils/enums/common';
import useHttp from 'utils/hooks/use-http';
import useUnmoutedFlag from 'utils/hooks/use-unmounted-flag';
import { MFormPageProps } from 'views/MForm/MForm.model';

import {
  IUserProfilePersonalInfoFormFields,
  userProfilePersonalInfoInitialValues,
  userProfilePersonalInfoValidationSchema,
} from './PersonalInfoTab.model';
import PersonalInfoTabView from './PersonalInfoTab.view';

type Props = MFormPageProps;

const PersonalInfoTab: React.FC<Props> = () => {
  const { t } = useTranslation();
  const http = useHttp();
  const dispatch = useDispatch();
  const unmountedFlagRef = useUnmoutedFlag();
  const sessionData: ISession = useSelector((state: AppState) => state.auth.session!);
  const isCompanyAdmin = !!useSelector((app: AppState) => app.auth.session?.isCompanyAdmin);
  const [userIdState, setUserIdState] = useState<string>('');
  const [isLoadingState, setIsLoadingState] = useState<boolean>(false);

  const form: FormikContextType<IUserProfilePersonalInfoFormFields> = useFormik<IUserProfilePersonalInfoFormFields>({
    initialValues: userProfilePersonalInfoInitialValues,
    validationSchema: userProfilePersonalInfoValidationSchema,
    onSubmit(values: IUserProfilePersonalInfoFormFields) {
      if (!userIdState) {
        return;
      }

      const currentUser: IUpdateUserRequest = {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || null /** Null is a flag to unset the field */,
        gender: values.gender || null /** Null is a flag to unset the field */,
        dateOfBirth: values.dateOfBirth?.unix() || null /** Null is a flag to unset the field */,
      };

      setIsLoadingState(() => true);

      http.user
        .update(Number(userIdState), currentUser)
        .then(() => {
          if (unmountedFlagRef.current) {
            return;
          }

          dispatch(uiActions.showSuccessToast({ message: t('userProfile.toast.success') }));

          /** Renew session to reload user info */
          dispatch(authActions.autoLogin());
        })
        .catch((error) => {
          console.error(error);

          if (!unmountedFlagRef.current) {
            dispatch(uiActions.showErrorToast({ message: t('userProfile.toast.error') }));
          }
        })
        .finally(() => {
          if (!unmountedFlagRef.current) {
            setIsLoadingState(() => false);
          }
        });
    },
  });

  useEffect(() => {
    if (!sessionData?.accessToken) {
      return;
    }

    let abortController: AbortController | null = new AbortController();

    setIsLoadingState(() => true);

    http.auth
      .whoAmI(abortController, sessionData.accessToken)
      .then((me: IWhoAmIResponse) => {
        form.setValues(() => {
          return {
            firstName: me.firstName,
            lastName: me.lastName,
            email: me.email,
            phone: me.phone,
            gender: (me.gender || GenderTypes.Default) as GenderTypes,
            dateOfBirth: me.dateOfBirth !== undefined ? moment.unix(me.dateOfBirth) : null,
            isCompanyAdmin: me.isCompanyAdmin,
            access: me.access || [],
          };
        });

        setUserIdState(() => me.id);
      })
      .catch((error: string) => {
        console.error(error);
        dispatch(uiActions.showErrorToast({ message: t('userProfile.toast.error') }));
      })
      .finally(() => {
        abortController = null;
        setIsLoadingState(() => false);
      });

    return () => abortController?.abort();
  }, []);

  const genderOptions = [
    { id: GenderTypes.Default, label: t('userProfile.form.personalDetails.genderOptions.default') },
    { id: GenderTypes.Male, label: t('userProfile.form.personalDetails.genderOptions.male') },
    { id: GenderTypes.Female, label: t('userProfile.form.personalDetails.genderOptions.female') },
  ];

  return (
    <PersonalInfoTabView
      formInstance={form}
      isCompanyAdmin={isCompanyAdmin}
      isLoading={isLoadingState}
      genderOptions={genderOptions}
    ></PersonalInfoTabView>
  );
};

PersonalInfoTab.displayName = 'PersonalInfoTab';
PersonalInfoTab.defaultProps = {};

export default PersonalInfoTab;
